import { useEffect, useMemo, useRef, useState } from 'react'

import { collectRichTextEmbedEntryIds } from '../helpers/collectAssetLinkIds'
import { isContentfulPreviewFrame } from '../helpers/isContentfulPreviewFrame'
import {
  ContentfulPreviewAsset,
  ContentfulPreviewEntry,
  ContentfulPreviewPayload,
  fetchContentfulPreviewEntries,
} from '../services/contentfulPreviewService'

const NO_ENTRIES: ContentfulPreviewEntry[] = []
const NO_ASSETS: ContentfulPreviewAsset[] = []
const NO_IDS: string[] = []
const EMPTY: ContentfulPreviewPayload = { entries: NO_ENTRIES, assets: NO_ASSETS }

/**
 * Loads entries embedded in rich text that the page's include graph does not contain.
 *
 * An entry embedded after the page loaded is unknown to us, and unlike an asset it brings its own
 * links — a OneBlock points at a Copy, which points at an Image, which points at an Asset. So the
 * whole subtree is needed, which is exactly what `GET /__contentful-preview/:entryId` returns
 * (`include=10`). No new endpoint: an embedded entry is fetched the same way a page is.
 *
 * Without this, `buildGatsbyEntryReference` could name the type but not fill it in, and an embed
 * would render empty — worse than leaving it on build-time data.
 *
 * Each id is attempted once. An embed of an unsupported type, or a failed request, must not become
 * a fetch loop on every keystroke.
 */
export const useContentfulPreviewEmbeds = (liveEntries: ContentfulPreviewEntry[]): ContentfulPreviewPayload => {
  const [fetched, setFetched] = useState<ContentfulPreviewPayload>(EMPTY)
  const attempted = useRef(new Set<string>())

  const missingIds = useMemo(() => {
    if (!isContentfulPreviewFrame()) return NO_IDS

    const known = new Set([...liveEntries, ...fetched.entries].map(entry => entry.sys.id))

    return collectRichTextEmbedEntryIds(liveEntries).filter(id => !known.has(id) && !attempted.current.has(id))
  }, [liveEntries, fetched])

  useEffect(() => {
    if (!missingIds.length) return

    let isCurrent = true
    missingIds.forEach(id => attempted.current.add(id))

    Promise.all(
      missingIds.map(id =>
        fetchContentfulPreviewEntries(id).catch(error => {
          console.error(`Falling back to build time content for embedded entry ${id}.`, error)
          return undefined
        }),
      ),
    ).then(results => {
      if (!isCurrent) return

      const payloads = results.filter((payload): payload is ContentfulPreviewPayload => Boolean(payload))
      if (!payloads.length) return

      setFetched(current => ({
        entries: [...current.entries, ...payloads.flatMap(payload => payload.entries)],
        assets: [...current.assets, ...payloads.flatMap(payload => payload.assets)],
      }))
    })

    return () => {
      isCurrent = false
    }
  }, [missingIds])

  return fetched
}
